import type { FC } from 'react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css, cssMap, keyframes } from '@compiled/react';

import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import Lozenge from '@atlaskit/lozenge/new/lozenge';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { fg } from '@confluence/feature-gating';

import { useReasoningViewedAnalytics } from '../analytics.ts';

import { SuggestionAgentIdentity } from './SuggestionAgentIdentity.tsx';
import { SuggestionItemSourceAppStack } from './SuggestionItemSourceAppStack.tsx';
import { SuggestionItemSources } from './SuggestionItemSources.tsx';

const i18n = defineMessages({
	accept: {
		id: 'suggestions-panel.accept',
		defaultMessage: 'Accept',
		description: 'Label for the button that accepts a suggestion',
	},
	discard: {
		id: 'suggestions-panel.discard',
		defaultMessage: 'Discard',
		description: 'Label for the button that discards a suggestion',
	},
	goodSuggestion: {
		id: 'suggestions-panel.good-suggestion',
		defaultMessage: 'Good suggestion',
		description:
			'Accessible label / tooltip for the thumbs up button that submits positive feedback on a suggestion',
	},
	badSuggestion: {
		id: 'suggestions-panel.bad-suggestion',
		defaultMessage: 'Bad suggestion',
		description:
			'Accessible label / tooltip for the thumbs down button that submits negative feedback on a suggestion',
	},
	dismiss: {
		id: 'suggestions-panel.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Label for the button that dismisses a suggestion',
	},
	restrictedLabel: {
		id: 'suggestions-panel.restricted-label',
		defaultMessage: 'Restricted',
		description: 'Accessible label for the padlock lozenge on the suggestion card',
	},
	restrictedTooltip: {
		id: 'suggestions-panel.restricted-tooltip',
		defaultMessage: 'Only editors with access to the source can see this suggestion',
		description: 'Tooltip for the padlock lozenge on the suggestion card',
	},
	moreActions: {
		id: 'suggestions-panel.more-actions',
		defaultMessage: 'More actions',
		description:
			'Accessible label and tooltip for the menu containing additional suggestion actions',
	},
});

export const SUGGESTION_ITEM_DESCRIPTION_SHIMMER_TESTID = 'suggestion-item-description-shimmer';

// Shimmer applied to the description while the suggestion is regenerating.
// Copied from the editor's SuggestionCard (itself a copy of
// rovo-platform-ui-components' ShimmerText) rather than using `ShimmerText`
// directly: that component hardcodes its own Text size/colour, which would
// change the description's typography. This wraps the existing Text untouched.
const shimmerAnimation = keyframes({
	'0%': {
		backgroundPosition: '200% 0',
	},
	'100%': {
		backgroundPosition: '-200% 0',
	},
});

const shimmerGradient = css({
	background: `linear-gradient(
		90deg,
		${token('color.text.subtlest')} 0%,
		${token('color.text.subtlest')} 20%,
		#c7cddc 40%,
		${token('color.text.subtlest')} 60%,
		${token('color.text.subtlest')} 80%,
		#c7cddc 90%,
		${token('color.text.subtlest')} 100%
	)`,
	backgroundSize: '200% 100%',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	animation: `${shimmerAnimation} 4s linear infinite`,
});

const styles = cssMap({
	item: {
		width: '100%',
		boxSizing: 'border-box',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		borderRadius: token('radius.xlarge'),
		paddingInline: token('space.200'),
		paddingBlock: token('space.200'),
		backgroundColor: token('elevation.rovo.surface.overlay'),
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'start',
		justifyContent: 'start',
		textAlign: 'start',
		gap: token('space.150'),
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('elevation.rovo.surface.overlay'),
			boxShadow: token('elevation.shadow.overflow'),
		},
		'&:active': {
			backgroundColor: token('elevation.rovo.surface.overlay'),
			transition: token('motion.listitem.pressed'),
		},
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
			outlineOffset: token('space.025'),
		},
	},
	itemSelected: {
		cursor: 'auto',
		backgroundColor: token('elevation.rovo.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('elevation.rovo.surface.overlay'),
			boxShadow: token('elevation.shadow.overlay'),
		},
		'&:active': {
			backgroundColor: token('elevation.rovo.surface.overlay'),
			transition: token('motion.listitem.pressed'),
		},
	},
	itemMotion: {
		transition: `${token('motion.button.hovered')}, box-shadow ${token(
			'motion.duration.medium',
		)} ${token('motion.easing.out.practical')}`,
	},
	itemLegacy: {
		transition: `background-color ${token('motion.duration.medium')} ${token(
			'motion.easing.out.practical',
		)}, box-shadow ${token('motion.duration.medium')} ${token('motion.easing.out.practical')}`,
	},
	detailsToggle: {
		display: 'flex',
		alignItems: 'center',
		color: token('color.text.subtlest'),
		backgroundColor: 'transparent',
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		cursor: 'pointer',
		border: 'none',
	},
	cardRow: {
		width: '100%',
	},
	detailsContainer: {
		width: '100%',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		// First-listed layers paint on top: scrims, then the grid and base colour.
		backgroundImage: `linear-gradient(
			to bottom,
			transparent 0%,
			${token('color.background.accent.gray.subtlest')} 100%
		), linear-gradient(
			to left,
			transparent 0%,
			${token('color.background.accent.gray.subtlest')} 100%
		), linear-gradient(
			${token('color.background.neutral')} ${token('border.width')},
			transparent ${token('border.width')}
		), linear-gradient(
			90deg,
			${token('color.background.neutral')} ${token('border.width')},
			transparent ${token('border.width')}
		)`,
		backgroundSize: `100% 100%, 100% 100%, ${token('space.400')} ${token(
			'space.400',
		)}, ${token('space.400')} ${token('space.400')}`,
		// Offset by one line width so no grid line sits on the container's edge.
		backgroundPosition: `0 0, 0 0, calc(-1 * ${token('border.width')}) calc(-1 * ${token(
			'border.width',
		)}), calc(-1 * ${token('border.width')}) calc(-1 * ${token('border.width')})`,
		borderRadius: token('radius.large'),
		paddingBlock: token('space.150'),
	},
	detailsContent: {
		paddingInline: token('space.150'),
	},
	privateMetadata: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	},
	restrictedMetadata: {
		alignItems: 'center',
		cursor: 'default',
		display: 'flex',
		overflow: 'hidden',
	},
	restrictedContent: {
		alignItems: 'center',
		columnGap: token('space.0'),
		display: 'inline-grid',
		gridTemplateColumns: 'auto minmax(0, 0fr)',
		marginInlineEnd: `calc(-1 * ${token('border.width')})`,
		marginInlineStart: `calc(-1 * ${token('border.width')})`,
		transitionDuration: token('motion.duration.medium'),
		transitionProperty: 'grid-template-columns, column-gap, margin-inline-start, margin-inline-end',
		transitionTimingFunction: token('motion.easing.inout.bold'),
		verticalAlign: 'middle',
	},
	restrictedContentExpanded: {
		columnGap: token('space.050'),
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		marginInlineEnd: token('space.0'),
		marginInlineStart: token('space.0'),
	},
	restrictedLabel: {
		minWidth: token('space.0'),
		opacity: 0,
		overflow: 'hidden',
		transitionDuration: token('motion.duration.medium'),
		transitionProperty: 'opacity',
		transitionTimingFunction: token('motion.easing.out.practical'),
		whiteSpace: 'nowrap',
	},
	restrictedLabelExpanded: {
		opacity: 1,
	},
});

export type SuggestionItemProps = {
	suggestion: Suggestion;
	isSelected?: boolean;
	/**
	 * The suggestion is being regenerated (stale check → regenerate). While true,
	 * the description shimmers and the action buttons are disabled so the user
	 * can't act on about-to-be-replaced content.
	 */
	isRegenerating?: boolean;
	onSelect?: (id: string) => void;
	onAccept?: (id: string, isDiffHidden: boolean) => void;
	onDiscard?: (id: string) => void;
	onDismiss?: (id: string) => void;
	onGoodSuggestion?: (id: string) => void;
	onBadSuggestion?: (id: string) => void;
	onToggleDeletedDiffs?: (id: string, hideDeletedDiffs: boolean) => void;
};

export const SuggestionItem: FC<SuggestionItemProps> = ({
	suggestion,
	isSelected = false,
	isRegenerating = false,
	onSelect,
	onAccept,
	onDiscard,
	onDismiss,
	onGoodSuggestion,
	onBadSuggestion,
	onToggleDeletedDiffs,
}) => {
	const ref = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		/**
		 * Bring the selected item fully into view — when the sidebar opens with a suggestion
		 * already selected in the editor, and when the clicked item is only part-way visible
		 * or sitting under the footer scrim.
		 *
		 * `nearest` scrolls the minimum needed and is a no-op once the item is inside the
		 * scrollport's optimal viewing region, which excludes the footer scrim (see
		 * `scrollPaddingBlockEnd` in `SuggestionsPanelLayout`) — so no manual visibility
		 * check is needed.
		 */
		if (isSelected) {
			ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [isSelected]);
	const { formatMessage } = useIntl();
	const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
	const [isRestrictedHovered, setIsRestrictedHovered] = useState(false);
	const fireReasoningViewedAnalytics = useReasoningViewedAnalytics();

	// An out-of-date suggestion can no longer be safely applied — only discard.
	const isStale = suggestion.status === 'STALE';

	const [isHidingDeletedDiffs, setIsHidingDeletedDiffs] = useState(false);
	useEffect(() => {
		if (!isSelected) {
			setIsHidingDeletedDiffs(false);
		}
	}, [isSelected]);

	const handleDetailsToggle = useCallback(
		(e: React.MouseEvent) => {
			// Prevent toggling selection when clicking the details toggle
			e.stopPropagation();
			if (!isDetailsExpanded) {
				fireReasoningViewedAnalytics(suggestion);
			}
			setIsDetailsExpanded((prev) => !prev);
		},
		[fireReasoningViewedAnalytics, isDetailsExpanded, suggestion],
	);

	const handleSelect = useCallback(() => {
		// Once selected, users have to select another suggestion to deselect
		if (isSelected) return;
		onSelect?.(suggestion.id);
	}, [onSelect, suggestion.id, isSelected]);

	const handleAccept = useCallback(
		() => onAccept?.(suggestion.id, isHidingDeletedDiffs),
		[isHidingDeletedDiffs, onAccept, suggestion.id],
	);

	const handleDiscard = useCallback(() => onDiscard?.(suggestion.id), [onDiscard, suggestion.id]);

	const handleDismiss = useCallback(() => onDismiss?.(suggestion.id), [onDismiss, suggestion.id]);

	const handleGoodSuggestion = useCallback(
		() => onGoodSuggestion?.(suggestion.id),
		[onGoodSuggestion, suggestion.id],
	);

	const handleBadSuggestion = useCallback(
		() => onBadSuggestion?.(suggestion.id),
		[onBadSuggestion, suggestion.id],
	);

	const handleToggleDeletedDiffs = useCallback(() => {
		const updatedHideDeletedDiffs = !isHidingDeletedDiffs;
		setIsHidingDeletedDiffs(updatedHideDeletedDiffs);
		onToggleDeletedDiffs?.(suggestion.id, updatedHideDeletedDiffs);
	}, [isHidingDeletedDiffs, onToggleDeletedDiffs, suggestion.id]);

	const renderMoreOptionsTrigger = useCallback(
		({
			triggerRef,
			'aria-controls': ariaControls,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHasPopup,
			onClick,
			isSelected: isMenuSelected,
		}: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				ref={triggerRef}
				aria-controls={ariaControls}
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHasPopup}
				isDisabled={isRegenerating}
				isSelected={isMenuSelected}
				onClick={onClick}
				icon={ShowMoreHorizontalIcon}
				label={formatMessage(i18n.moreActions)}
				isTooltipDisabled={false}
				appearance="subtle"
				spacing="default"
			/>
		),
		[formatMessage, isRegenerating],
	);

	const isInsight = suggestion.actionKind === 'INFORMATIONAL';
	const hasDetails = Boolean(suggestion.reasoning || suggestion.sources?.length);
	const hasSources = Boolean(suggestion.sources?.length);
	const restrictedMetadata = hasSources && (
		<Tooltip
			position="top"
			content={formatMessage(i18n.restrictedTooltip)}
			ignoreTooltipPointerEvents
		>
			<span
				css={[styles.privateMetadata, styles.restrictedMetadata]}
				data-testid="suggestion-restricted-metadata"
				onMouseEnter={() => setIsRestrictedHovered(true)}
				onMouseLeave={() => setIsRestrictedHovered(false)}
				onFocus={() => setIsRestrictedHovered(true)}
				onBlur={() => setIsRestrictedHovered(false)}
			>
				<Lozenge appearance="neutral">
					<span
						css={[
							styles.restrictedContent,
							isRestrictedHovered && styles.restrictedContentExpanded,
						]}
					>
						<LockLockedIcon label="" size="small" color={token('color.icon.subtle')} />
						<span
							css={[styles.restrictedLabel, isRestrictedHovered && styles.restrictedLabelExpanded]}
						>
							<Text as="span" size="small" weight="regular" color="color.text.subtle">
								{formatMessage(i18n.restrictedLabel)}
							</Text>
						</span>
					</span>
				</Lozenge>
			</span>
		</Tooltip>
	);

	return (
		<button
			ref={ref}
			css={[
				styles.item,
				isSelected && styles.itemSelected,
				fg('platform-dst-motion-uplift-custom-button') && styles.itemMotion,
				!fg('platform-dst-motion-uplift-custom-button') && styles.itemLegacy,
			]}
			onClick={handleSelect}
			aria-pressed={isSelected}
		>
			<Stack space="space.150" xcss={styles.cardRow}>
				{isRegenerating ? (
					<div css={shimmerGradient} data-testid={SUGGESTION_ITEM_DESCRIPTION_SHIMMER_TESTID}>
						<Text as="p" size="medium" weight="medium">
							{suggestion.description}
						</Text>
					</div>
				) : (
					<Text as="p" size="medium" weight="medium">
						{suggestion.description}
					</Text>
				)}
				{isSelected && hasDetails && (
					<Stack space="space.100" xcss={styles.cardRow}>
						<Inline alignBlock="center" spread="space-between" xcss={styles.cardRow}>
							<Pressable
								xcss={styles.detailsToggle}
								onClick={handleDetailsToggle}
								isDisabled={isRegenerating}
								aria-expanded={isDetailsExpanded}
							>
								<Inline as="span" space="space.075" alignBlock="center">
									<Text size="medium" weight="medium" color="inherit">
										{formatMessage(aiSuggestionsMessages.cardDetailsButtonLabel)}
									</Text>
									{isDetailsExpanded ? (
										<ChevronDownIcon label="" size="small" />
									) : (
										<ChevronRightIcon label="" size="small" />
									)}
								</Inline>
							</Pressable>
							<Inline space="space.050" alignBlock="center">
								{restrictedMetadata}
								<SuggestionItemSourceAppStack sources={suggestion.sources} />
							</Inline>
						</Inline>
						{isDetailsExpanded && (
							<Stack xcss={styles.detailsContainer} space="space.250">
								{suggestion.reasoning && (
									<div css={styles.detailsContent}>
										<Text as="p" color="color.text.subtle">
											{suggestion.reasoning}
										</Text>
									</div>
								)}
								{!!suggestion.sources?.length && (
									<SuggestionItemSources sources={suggestion.sources} />
								)}
							</Stack>
						)}
					</Stack>
				)}
			</Stack>
			{isSelected && (
				<Inline alignBlock="center" spread="space-between" xcss={styles.cardRow}>
					<SuggestionAgentIdentity
						agent={suggestion.createdByAgent}
						user={suggestion.createdByUser}
					/>
					<Inline space="space.050" alignBlock="center">
						{isInsight ? (
							<Button onClick={handleDismiss} isDisabled={isRegenerating}>
								{formatMessage(i18n.dismiss)}
							</Button>
						) : (
							<React.Fragment>
								{(onToggleDeletedDiffs || onGoodSuggestion || onBadSuggestion) && (
									<DropdownMenu<HTMLButtonElement>
										placement="bottom-end"
										trigger={renderMoreOptionsTrigger}
									>
										<DropdownItemGroup>
											{onToggleDeletedDiffs && (
												<DropdownItem
													elemBefore={<HistoryIcon label="" />}
													onClick={handleToggleDeletedDiffs}
												>
													{isHidingDeletedDiffs
														? formatMessage(aiSuggestionsMessages.cardShowDeletedDiffsLabel)
														: formatMessage(aiSuggestionsMessages.cardHideDeletedDiffsLabel)}
												</DropdownItem>
											)}
											{onGoodSuggestion && (
												<DropdownItem
													elemBefore={<ThumbsUpIcon label="" />}
													onClick={handleGoodSuggestion}
												>
													{formatMessage(i18n.goodSuggestion)}
												</DropdownItem>
											)}
											{onBadSuggestion && (
												<DropdownItem
													elemBefore={<ThumbsDownIcon label="" />}
													onClick={handleBadSuggestion}
												>
													{formatMessage(i18n.badSuggestion)}
												</DropdownItem>
											)}
										</DropdownItemGroup>
									</DropdownMenu>
								)}
								<Button
									appearance={isStale ? 'primary' : 'subtle'}
									onClick={handleDiscard}
									isDisabled={isRegenerating}
								>
									{formatMessage(i18n.discard)}
								</Button>
								{/* A stale suggestion can only be discarded — hide Accept. */}
								{!isStale && (
									<Button appearance="primary" onClick={handleAccept} isDisabled={isRegenerating}>
										{formatMessage(i18n.accept)}
									</Button>
								)}
							</React.Fragment>
						)}
					</Inline>
				</Inline>
			)}
			{!isSelected && (
				<Inline alignBlock="center" spread="space-between" xcss={styles.cardRow}>
					<SuggestionAgentIdentity
						agent={suggestion.createdByAgent}
						user={suggestion.createdByUser}
					/>
					<Inline space="space.050" alignBlock="center">
						{restrictedMetadata}
						<SuggestionItemSourceAppStack sources={suggestion.sources} />
					</Inline>
				</Inline>
			)}
		</button>
	);
};
